import { AUFTRAG_API } from '@Constants/backendConstants';
import { type ApiContainerFollowUpOffer, type ApiPropagateContainerResult } from '@Model';
import {
    Button,
    Checkbox,
    FlexColumn,
    FlexRow,
    isOk,
    ModalIconTitle,
    RadioButton,
    type Result,
    useSynqoHttp,
    useToasts,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconRoute } from '@tabler/icons-react';
import { useState } from 'react';
import classes from './ContainerFollowUpModal.module.css';
import {
    changeLines,
    chosenStops,
    followUpEvent,
    type PropagateContainerCommand,
    propagationSummary,
} from './containerFollowUpOffer';
import { OrderLink } from './OrderLink';

interface ContainerFollowUpModalProps {
    offer: ApiContainerFollowUpOffer;
    /** Closes the modal, after carrying over or without. */
    onDone: () => void;
    /** Opens an order of a chain on top of the modal. */
    onOpenOrder: (orderId: string) => void;
}

const NONE = -1;

const QUESTIONS = {
    change: {
        order: 'Soll die Änderung in den folgenden Auftrag übernommen werden?',
        orders: 'Soll die Änderung in die folgenden Aufträge übernommen werden?',
    },
    changes: {
        order: 'Sollen die Änderungen in den folgenden Auftrag übernommen werden?',
        orders: 'Sollen die Änderungen in die folgenden Aufträge übernommen werden?',
    },
} as const;

/** Asks which pickup and which emptyings take over the changed container values of a placement. */
// @ai-generated 2026-10-30T14:40:58+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function ContainerFollowUpModal({ offer, onDone, onOpenOrder }: ContainerFollowUpModalProps) {
    const i18n = useTranslate('');
    const http = useSynqoHttp(AUFTRAG_API);
    const { pushToast, pushAllToasts } = useToasts();
    const single = offer.choices.length === 1;
    const [choice, setChoice] = useState(single ? 0 : NONE);
    const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set(single ? offer.choices[0].before : []));
    const [sending, setSending] = useState(false);
    const chosen = choice === NONE ? undefined : offer.choices[choice];
    const stops = chosenStops(offer, chosen, ticked);
    const pickups = offer.choices.filter((option) => option.pickup).length;
    const changes = changeLines(offer.source);
    const containers = offer.source.container_action_ids.length;
    const type = offer.source.container_type.display_string;
    const title = containers > 1 ? `${containers} × ${type}` : type;
    const singleOrder = offer.emptyings.length + pickups === 1;
    const question = QUESTIONS[changes.length === 1 ? 'change' : 'changes'][singleOrder ? 'order' : 'orders'];

    const choose = (index: number) => {
        setChoice(index);
        setTicked(new Set(offer.choices[index].before));
    };

    const tick = (orderId: string, on: boolean) => {
        const next = new Set(ticked);
        if (on) {
            next.add(orderId);
        } else {
            next.delete(orderId);
        }
        setTicked(next);
    };

    const apply = async () => {
        setSending(true);
        const { source } = offer;
        const result = await http.post<Result<ApiPropagateContainerResult>, PropagateContainerCommand>(
            '/orders:propagate_container.v1',
            {
                order_id: source.order_id,
                container_action_ids: source.container_action_ids,
                old_container_type: source.old_container_type,
                old_container: source.old_container,
                old_content: source.old_content,
                old_billed_as: source.old_billed_as,
                stops,
            },
        );
        setSending(false);
        if (isOk(result)) {
            pushToast({
                message: propagationSummary(result.data),
                variant: result.data.skipped.length > 0 ? 'warning' : 'success',
            });
        } else {
            pushAllToasts(result.messages);
        }
        onDone();
    };

    return (
        <FlexColumn gap="lg" align="stretch" className={classes.body}>
            <ModalIconTitle icon={<IconRoute />} title={title} />
            <FlexColumn gap="xxs" align="stretch">
                <FlexRow className={classes.heading}>
                    {changes.length === 1 ? i18n('Änderung') : i18n('Änderungen')}
                </FlexRow>
                {changes.map((line) => (
                    <FlexRow key={line}>{line}</FlexRow>
                ))}
            </FlexColumn>
            <FlexRow>{i18n(question)}</FlexRow>
            {pickups > 0 && (
                <FlexColumn gap="xs" align="stretch">
                    <FlexRow className={classes.heading}>{i18n('Abholung')}</FlexRow>
                    {offer.choices.map((option, index) => (
                        <FlexRow key={option.pickup?.order_id ?? 'none'} gap="xs" align="center">
                            <RadioButton
                                name="container_follow_up_pickup"
                                label=""
                                value={choice === index}
                                onChange={() => {
                                    choose(index);
                                    return true;
                                }}
                            />
                            {option.pickup ? (
                                <>
                                    <OrderLink
                                        orderId={option.pickup.order_id}
                                        number={option.pickup.order_number}
                                        accent
                                        onOpen={onOpenOrder}
                                    />
                                    <FlexRow>
                                        {followUpEvent(option.pickup, offer.source.container.display_string)}
                                    </FlexRow>
                                </>
                            ) : (
                                <FlexRow>{i18n('Keine Abholung')}</FlexRow>
                            )}
                        </FlexRow>
                    ))}
                </FlexColumn>
            )}
            {offer.emptyings.length > 0 && (
                <FlexColumn gap="xs" align="stretch">
                    <FlexRow className={classes.heading}>{i18n('Leerungen')}</FlexRow>
                    {offer.emptyings.map((emptying) => (
                        <FlexRow key={emptying.order_id} gap="xs" align="center">
                            <Checkbox
                                name={`container_follow_up_emptying_${emptying.order_id}`}
                                label=""
                                value={ticked.has(emptying.order_id)}
                                locked={!chosen || chosen.after.includes(emptying.order_id)}
                                onChange={(on) => {
                                    tick(emptying.order_id, on);
                                    return true;
                                }}
                            />
                            <OrderLink
                                orderId={emptying.order_id}
                                number={emptying.order_number}
                                accent
                                onOpen={onOpenOrder}
                            />
                            <FlexRow>{followUpEvent(emptying, offer.source.container.display_string)}</FlexRow>
                        </FlexRow>
                    ))}
                </FlexColumn>
            )}
            <FlexRow justify="center" gap="sm">
                <Button appearance="outlined" variant="normal" locked={sending} onClick={onDone}>
                    {i18n('Nicht übernehmen')}
                </Button>
                <Button locked={sending || stops.length === 0} onClick={() => void apply()}>
                    {i18n('Übernehmen')}
                </Button>
            </FlexRow>
        </FlexColumn>
    );
}
